import { itx } from "../../../src/translationItx/itx";
import React, { useId } from "react";

export type WallOutletSlotType =
  | "schuko"
  | "schukoSwitched"
  | "lan"
  | "lan2"
  | "tv"
  | "usb"
  | "switch1"
  | "switch2"
  | "switch4";

export interface WallOutletSvgProps {
  slots: WallOutletSlotType[];
  /** Optionale mittige Kennzeichnung je Steckplatz. */
  slotLabels?: Array<string | undefined>;
  /** Mischt einen dezenten Auswahlton direkt in die Darstellung ein. */
  selected?: boolean;
  /** Mischt einen schwachen Auswahlton für eine indirekt ausgewählte Kombination ein. */
  indirectlySelected?: boolean;
  /** Hebt ein einzelnes Modul innerhalb einer Kombination hervor. */
  selectedSlotIndex?: number;
  /** SVG-Filter für den direkt ausgewählten Steckplatz. */
  selectedSlotFilter?: string;
  /** Index des aktuell überfahrenen Steckplatzes. */
  hoveredSlotIndex?: number;
  /** Dreht nur den Einsatzinhalt innerhalb jedes Moduls. */
  contentRotation?: number;
  moduleSize?: number;
  gap?: number;
  padding?: number;
  className?: string;
  style?: React.CSSProperties;
  x?: number;
  y?: number;
  width?: number | string;
  height?: number | string;
  /** Reserviert innerhalb des Rahmens einen weiteren, vom Inline-Editor gezeichneten Steckplatz. */
  extraSlot?: boolean;
}

interface ModuleProps {
  x: number;
  y: number;
  width: number;
  height: number;
  gradientIds: {
    insert: string;
    socket: string;
    rocker: string;
    shadow: string;
  };
}

interface SwitchButtonsProps extends ModuleProps {
  columns: number;
  rows: number;
}

export function wallOutletLayout(slotCount: number, moduleSize = 100, gap = 6, padding = 10) {
  const frameWidth = padding * 2 + slotCount * moduleSize + Math.max(0, slotCount - 1) * gap;
  const frameHeight = moduleSize + padding * 2;
  return {
    frameWidth,
    frameHeight,
    slots: Array.from({ length: slotCount }, (_, index) => ({
      x: padding + index * (moduleSize + gap),
      y: padding,
      width: moduleSize,
      height: moduleSize,
    })),
    nextSlot: {
      x: padding + slotCount * (moduleSize + gap),
      y: padding,
      width: moduleSize,
      height: moduleSize,
    },
  };
}

/** Extends the existing SVG footprint to the right without re-centering or rescaling its slots. */
export function wallOutletPreviewBounds(
  geometry: { x: number; y: number; width: number; height: number },
  slotCount: number,
) {
  const existing = wallOutletLayout(slotCount);
  const preview = wallOutletLayout(slotCount + 1);
  const scale = Math.min(geometry.width / existing.frameWidth, geometry.height / existing.frameHeight);
  return {
    x: geometry.x + (geometry.width - existing.frameWidth * scale) / 2,
    y: geometry.y + (geometry.height - existing.frameHeight * scale) / 2,
    width: preview.frameWidth * scale,
    height: preview.frameHeight * scale,
  };
}

/**
 * Parametrische Frontansicht einer Mehrfach-Kombination aus:
 * - Schuko-Steckdose
 * - LAN/RJ45
 * - USB-A + USB-C
 * - 1-fach Schalter
 * - 2-fach Schalter
 * - 4-fach Schalter
 *
 * Die Anzahl der Module wird automatisch aus `slots` ermittelt.
 * Beispiel:
 *
 * <WallOutletSvg
 *   slots={[
 *     "schuko",
 *     "lan",
 *     "usb",
 *     "switch2",
 *     "switch4"
 *   ]}
 * />
 */
export function WallOutletSvg({
  slots,
  slotLabels = [],
  selected = false,
  indirectlySelected = false,
  selectedSlotIndex,
  selectedSlotFilter,
  hoveredSlotIndex,
  contentRotation = 0,
  moduleSize = 100,
  gap = 6,
  padding = 10,
  className,
  style,
  x,
  y,
  width = "100%",
  height = "100%",
  extraSlot = false,
}: WallOutletSvgProps) {
  const visibleSlots = slots;
  const uid = useId().replace(/:/g, "");
  const gradientIds = {
    frame: `${uid}-wall-outlet-frame`,
    insert: `${uid}-wall-outlet-insert`,
    socket: `${uid}-wall-outlet-socket`,
    rocker: `${uid}-wall-outlet-rocker`,
    shadow: `${uid}-wall-outlet-shadow`,
  };

  const layout = wallOutletLayout(visibleSlots.length + (extraSlot ? 1 : 0), moduleSize, gap, padding);
  const { frameWidth, frameHeight } = layout;
  const selectionColor = (selectedColor: string, indirectColor: string, defaultColor: string) =>
    selected ? selectedColor : indirectlySelected ? indirectColor : defaultColor;

  const renderModule = (
    type: WallOutletSlotType,
    x: number,
    index: number,
  ) => {
    const common: ModuleProps = {
      x,
      y: padding,
      width: moduleSize,
      height: moduleSize,
      gradientIds,
    };

    return (
      <g
        key={`${type}-${index}`}
        className={`C1_wallOutletSlot${selectedSlotIndex === index ? " C1_wallOutletSlot--selected" : ""}${hoveredSlotIndex === index && selectedSlotIndex !== index ? " C1_eloItemHover" : ""}`}
        filter={selectedSlotIndex === index ? selectedSlotFilter : undefined}
      >
        <ModuleBase {...common} />

        <g transform={`rotate(${-contentRotation} ${x + moduleSize / 2} ${padding + moduleSize / 2})`}>
          {(type === "schuko" || type === "schukoSwitched") && <Schuko {...common} />}
          {type === "schukoSwitched" && <SwitchedMarker {...common} />}
          {type === "lan" && <Lan {...common} />}
          {type === "lan2" && <LanDuplex {...common} />}
          {type === "tv" && <TvCoax {...common} />}
          {type === "usb" && <Usb {...common} />}
          {type === "switch1" && <Switch1 {...common} />}
          {type === "switch2" && <Switch2 {...common} />}
          {type === "switch4" && <Switch4 {...common} />}
          {slotLabels[index] && (
            <text
              className="C1_wallOutletSlotLabel"
              x={x + moduleSize / 2}
              y={padding + moduleSize / 2}
              textAnchor="middle"
              dominantBaseline="central"
              pointerEvents="none"
            >
              {slotLabels[index]}
            </text>
          )}
        </g>
      </g>
    );
  };

  if (visibleSlots.length === 0) {
    return null;
  }

  return (
    <svg
      viewBox={`0 0 ${frameWidth} ${frameHeight}`}
      x={x}
      y={y}
      width={width}
      height={height}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={gradientIds.frame} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={selectionColor("#fff4df", "#fffaf5", "#fafafa")} />
          <stop offset="55%" stopColor={selectionColor("#f5bd62", "#f2e4da", "#eeeeee")} />
          <stop offset="100%" stopColor={selectionColor("#d98a22", "#dfc8b8", "#d8d8d8")} />
        </linearGradient>

        <linearGradient
          id={gradientIds.insert}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop offset="0%" stopColor={selectionColor("#fff8e9", "#fffaf6", "#ffffff")} />
          <stop offset="55%" stopColor={selectionColor("#ffd898", "#f4e7dc", "#eeeeee")} />
          <stop offset="100%" stopColor={selectionColor("#eda744", "#e2cbbb", "#d7d7d7")} />
        </linearGradient>

        <radialGradient
          id={gradientIds.socket}
          cx="45%"
          cy="38%"
          r="70%"
        >
          <stop offset="0%" stopColor={selectionColor("#fff8ec", "#fffaf6", "#fafafa")} />
          <stop offset="65%" stopColor={selectionColor("#ffdca1", "#f1e3d8", "#e5e5e5")} />
          <stop offset="100%" stopColor={selectionColor("#e9ad55", "#cdb7a7", "#b9b9b9")} />
        </radialGradient>

        <linearGradient
          id={gradientIds.rocker}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop offset="0%" stopColor={selectionColor("#fffaf0", "#fffbf7", "#ffffff")} />
          <stop offset="72%" stopColor={selectionColor("#ffe0ad", "#f4e8df", "#eeeeee")} />
          <stop offset="100%" stopColor={selectionColor("#efb35b", "#ddc7b8", "#d0d0d0")} />
        </linearGradient>

        <filter
          id={gradientIds.shadow}
          x="-30%"
          y="-30%"
          width="160%"
          height="160%"
        >
          <feDropShadow
            dx="0"
            dy="2"
            stdDeviation="2"
            floodColor="#000000"
            floodOpacity="0.22"
          />
        </filter>
      </defs>

      <rect
        x={1}
        y={1}
        width={frameWidth - 2}
        height={frameHeight - 2}
        rx={9}
        fill={`url(#${gradientIds.frame})`}
        stroke="#b2b2b2"
        strokeWidth={1.2}
        filter={`url(#${gradientIds.shadow})`}
      />

      <path
        d={`M 10 5 H ${frameWidth - 10}`}
        stroke="#ffffff"
        strokeOpacity={0.8}
      />

      {visibleSlots.map((slot, index) => renderModule(slot, layout.slots[index].x, index))}
    </svg>
  );
}

function ModuleBase({ x, y, width, height, gradientIds }: ModuleProps) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={5}
        fill={`url(#${gradientIds.insert})`}
        stroke="#bbbbbb"
      />

      <path
        d={`M ${x + 5} ${y + 4} H ${x + width - 5}`}
        stroke="#ffffff"
        strokeOpacity={0.9}
      />
    </g>
  );
}

function Schuko({ x, y, width, height, gradientIds }: ModuleProps) {
  const cx = x + width / 2;
  const cy = y + height / 2;
  const r = width * 0.33;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={`url(#${gradientIds.socket})`}
        stroke="#b5b5b5"
        strokeWidth={1.2}
      />

      <circle
        cx={cx - width * 0.13}
        cy={cy}
        r={width * 0.045}
        fill="#444"
      />

      <circle
        cx={cx + width * 0.13}
        cy={cy}
        r={width * 0.045}
        fill="#444"
      />

      <rect
        x={cx - width * 0.03}
        y={cy - r}
        width={width * 0.06}
        height={height * 0.07}
        rx={2}
        fill="#777"
      />

      <rect
        x={cx - width * 0.03}
        y={cy + r - height * 0.07}
        width={width * 0.06}
        height={height * 0.07}
        rx={2}
        fill="#777"
      />

      <ellipse
        cx={cx}
        cy={cy - height * 0.06}
        rx={width * 0.25}
        ry={height * 0.2}
        fill="#ffffff"
        opacity={0.14}
      />
    </g>
  );
}

/** Typischer On/Off-Schieber oben rechts für geschaltete Steckdosen. */
function SwitchedMarker({ x, y, width, height }: ModuleProps) {
  const trackX = x + width * 0.66;
  const trackY = y + height * 0.035;
  const trackWidth = width * 0.31;
  const trackHeight = height * 0.17;
  const knobRadius = trackHeight * 0.39;
  const knobX = trackX + trackWidth - trackHeight / 2;
  const knobY = trackY + trackHeight / 2;

  return (
    <g aria-label={itx("c1.ui.Geschaltet", "Geschaltet")}>
      <rect
        x={trackX}
        y={trackY}
        width={trackWidth}
        height={trackHeight}
        rx={trackHeight / 2}
        fill="#88a99a"
        stroke="#70877c"
        strokeWidth={0.9}
      />
      <circle
        cx={knobX}
        cy={knobY}
        r={knobRadius}
        fill="#f7f7f5"
        stroke="#879087"
        strokeWidth={0.8}
      />
      <line
        x1={trackX + trackHeight * 0.45}
        y1={trackY + trackHeight * 0.32}
        x2={trackX + trackHeight * 0.45}
        y2={trackY + trackHeight * 0.68}
        stroke="#f4f7f5"
        strokeWidth={1.2}
        strokeLinecap="round"
      />
    </g>
  );
}

function Lan({ x, y, width, height }: ModuleProps) {
  const bodyX = x + width * 0.24;
  const bodyY = y + height * 0.29;
  const bodyWidth = width * 0.52;
  const bodyHeight = height * 0.42;

  return (
    <g>
      <rect
        x={bodyX}
        y={bodyY}
        width={bodyWidth}
        height={bodyHeight}
        rx={4}
        fill="#dedede"
        stroke="#b2b2b2"
      />

      <rect
        x={x + width * 0.31}
        y={y + height * 0.36}
        width={width * 0.38}
        height={height * 0.26}
        fill="#303030"
      />

      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <line
          key={i}
          x1={x + width * (0.39 + i * 0.035)}
          y1={y + height * 0.4}
          x2={x + width * (0.39 + i * 0.035)}
          y2={y + height * 0.47}
          stroke="#cbb36f"
          strokeWidth={1.2}
        />
      ))}

      <text
        x={x + width / 2}
        y={y + height * 0.82}
        textAnchor="middle"
        fontSize={height * 0.08}
        fontFamily="Arial, sans-serif"
        fill="#777"
      >
        LAN
      </text>
    </g>
  );
}

function LanDuplex({ x, y, width, height }: ModuleProps) {
  const socketWidth = width * 0.34;
  const socketHeight = height * 0.35;
  const socketY = y + height * 0.3;
  const socketXs = [x + width * 0.11, x + width * 0.55];

  return (
    <g aria-label={itx("c1.ui.LAN_Duplex", "LAN Duplex")}>
      {socketXs.map((socketX) => (
        <g key={socketX}>
          <rect
            x={socketX}
            y={socketY}
            width={socketWidth}
            height={socketHeight}
            rx={3}
            fill="#dedede"
            stroke="#b2b2b2"
          />
          <rect
            x={socketX + socketWidth * 0.14}
            y={socketY + socketHeight * 0.17}
            width={socketWidth * 0.72}
            height={socketHeight * 0.58}
            fill="#303030"
          />
          {Array.from({ length: 8 }, (_, index) => (
            <line
              key={index}
              x1={socketX + socketWidth * (0.25 + index * 0.07)}
              y1={socketY + socketHeight * 0.28}
              x2={socketX + socketWidth * (0.25 + index * 0.07)}
              y2={socketY + socketHeight * 0.43}
              stroke="#cbb36f"
              strokeWidth={0.8}
            />
          ))}
        </g>
      ))}
      <text
        x={x + width / 2}
        y={y + height * 0.82}
        textAnchor="middle"
        fontSize={height * 0.08}
        fontFamily="Arial, sans-serif"
        fill="#777"
      >
        LAN 2
      </text>
    </g>
  );
}

function TvCoax({ x, y, width, height, gradientIds }: ModuleProps) {
  const cy = y + height * 0.5;
  const radius = width * 0.18;
  const connections = [x + width * 0.29, x + width * 0.71];

  return (
    <g aria-label={itx("c1.ui.TV_Koaxialanschlusse", "TV Koaxialanschlüsse")}>
      {connections.map((cx, index) => (
        <g key={cx}>
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill={`url(#${gradientIds.socket})`}
            stroke="#9d9d9d"
            strokeWidth={1.3}
          />
          <circle
            cx={cx}
            cy={cy}
            r={radius * 0.48}
            fill={index === 0 ? "#eeeeec" : "#555"}
            stroke="#888"
            strokeWidth={1}
          />
          {index === 0 && (
            <circle cx={cx} cy={cy} r={radius * 0.16} fill="#555" />
          )}
          <ellipse
            cx={cx - radius * 0.18}
            cy={cy - radius * 0.25}
            rx={radius * 0.42}
            ry={radius * 0.25}
            fill="#fff"
            opacity={0.22}
          />
        </g>
      ))}
    </g>
  );
}

function Usb({ x, y, width, height }: ModuleProps) {
  return (
    <g>
      <rect
        x={x + width * 0.2}
        y={y + height * 0.28}
        width={width * 0.6}
        height={height * 0.2}
        rx={3}
        fill="#e3e3e3"
        stroke="#b4b4b4"
      />

      <rect
        x={x + width * 0.3}
        y={y + height * 0.34}
        width={width * 0.4}
        height={height * 0.08}
        rx={1.5}
        fill="#333"
      />

      <rect
        x={x + width * 0.2}
        y={y + height * 0.55}
        width={width * 0.6}
        height={height * 0.2}
        rx={3}
        fill="#e3e3e3"
        stroke="#b4b4b4"
      />

      <rect
        x={x + width * 0.33}
        y={y + height * 0.61}
        width={width * 0.34}
        height={height * 0.08}
        rx={height * 0.04}
        fill="#333"
      />
    </g>
  );
}

function Switch1(props: ModuleProps) {
  return <SwitchButtons {...props} columns={1} rows={1} />;
}

function Switch2(props: ModuleProps) {
  return <SwitchButtons {...props} columns={2} rows={1} />;
}

function Switch4(props: ModuleProps) {
  return <SwitchButtons {...props} columns={2} rows={2} />;
}

function SwitchButtons({
  x,
  y,
  width,
  height,
  gradientIds,
  columns,
  rows,
}: SwitchButtonsProps) {
  const margin = width * 0.13;
  const innerWidth = width - margin * 2;
  const innerHeight = height - margin * 2;
  const gap = width * 0.03;

  const rockerWidth =
    (innerWidth - gap * (columns - 1)) / columns;

  const rockerHeight =
    (innerHeight - gap * (rows - 1)) / rows;

  return (
    <g>
      {Array.from({ length: rows }).map((_, row) =>
        Array.from({ length: columns }).map((_, column) => {
          const rockerX =
            x + margin + column * (rockerWidth + gap);

          const rockerY =
            y + margin + row * (rockerHeight + gap);

          return (
            <g key={`${row}-${column}`}>
              <rect
                x={rockerX}
                y={rockerY}
                width={rockerWidth}
                height={rockerHeight}
                rx={3}
                fill={`url(#${gradientIds.rocker})`}
                stroke="#bdbdbd"
                filter={`url(#${gradientIds.shadow})`}
              />

              <line
                x1={rockerX + 4}
                y1={rockerY + 4}
                x2={rockerX + rockerWidth - 4}
                y2={rockerY + 4}
                stroke="#ffffff"
                strokeOpacity={0.9}
              />
            </g>
          );
        }),
      )}
    </g>
  );
}

export default WallOutletSvg;
